import { useEffect, useCallback } from 'react';
import { useBlocker } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toggleAlertModule } from '../store/actions/alert.actions';

/**
 * Hook to guard against losing unsaved changes.
 * Intercepts browser navigation (back/forward/swipe), tab close, and React Router navigation.
 * Shows the existing DialogAlertSaveModule dialog when there are unsaved changes.
 *
 * @param {Object} options
 * @param {boolean} options.hasChanges - Whether there are unsaved changes
 * @param {Function} options.onSave - Function to call when user chooses to save
 * @param {string} [options.module='Maestro'] - Module name for the alert dialog
 * @param {string} [options.mode='editing'] - Mode string for the alert dialog
 */
const useUnsavedChangesGuard = ({ hasChanges = false, onSave, module = 'Maestro', mode = 'editing' }) => {
  const dispatch = useDispatch();

  // 1. Browser tab close / refresh - native browser dialog
  useEffect(() => {
    if (!hasChanges) return;

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      // Modern browsers show a generic message, returnValue is required for legacy
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasChanges]);

  // 2. React Router navigation (back/forward/swipe/link clicks)
  const blocker = useBlocker(useCallback(() => hasChanges, [hasChanges]));

  useEffect(() => {
    if (blocker.state !== 'blocked') return;

    // Show the existing save dialog
    dispatch(
      toggleAlertModule(
        true,
        module,
        mode,
        // Save: save changes then proceed with navigation
        () => {
          if (onSave) onSave();
          blocker.proceed();
        },
        // Don't Save: discard changes and proceed
        () => {
          blocker.proceed();
        }
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocker.state, dispatch, module, mode]);

  return blocker;
};

export default useUnsavedChangesGuard;
